import { useCallback, useContext } from 'react';
import { useRelayEnvironment, graphql, useFragment } from 'react-relay';
import CheckoutMutationWrapper from '../mutations/CheckoutMutationWrapper';
import { CheckoutServerVarsContext } from '../components/shared/CheckoutServerVarsContext';
import { useCheckoutSelector } from '../reducers/root';
import { logCheckoutError } from '../helpers/logCheckoutError';
import { useUpdateShipmentQuote_transaction$key } from './__generated__/useUpdateShipmentQuote_transaction.graphql';

const transactionFragment = graphql`
    fragment useUpdateShipmentQuote_transaction on Transaction {
        serviceId
    }
`;

type HookOptions = {
    transaction: useUpdateShipmentQuote_transaction$key;
};

type UpdateFunction = (args: {
    newShipmentQuoteId: string;
    newShipmentType: string;
    checkReturningBuyerShipment?: boolean;
}) => Promise<{ hasError: boolean }>;

export function useUpdateShipmentQuote(options: HookOptions): [UpdateFunction] {
    const { transaction: transactionRef } = options;
    const environment = useRelayEnvironment();

    const transaction = useFragment(transactionFragment, transactionRef);

    const shippingAddressId = useCheckoutSelector<string | undefined>(
        state => state.transaction.shippingAddressId
    );
    const zipCode = useCheckoutSelector(state => state.shipping.defaultZipCode);
    const previewZipCode = useCheckoutSelector(state => state.shipping.previewZipCode);
    const country = useCheckoutSelector(state => state.shipping.defaultCountry);
    const transactionId = transaction?.serviceId;
    const { isMobile } = useContext(CheckoutServerVarsContext);

    const updateShipmentQuote = useCallback<UpdateFunction>(
        async ({ newShipmentQuoteId, newShipmentType, checkReturningBuyerShipment }) => {
            let hasError = false;
            try {
                await CheckoutMutationWrapper.commit(
                    'TransactionShipmentQuote',
                    environment,
                    {
                        transactionId,
                        shipmentQuoteId: newShipmentQuoteId,
                        shipmentType: newShipmentType,
                        shippingAddressId,
                        zipCode: zipCode || previewZipCode,
                        country,
                        checkReturningBuyerShipment,
                    },
                    isMobile
                );
            } catch (e) {
                hasError = true;
                logCheckoutError(e, 'shipmentquote');
            }
            return { hasError };
        },
        [country, environment, isMobile, previewZipCode, shippingAddressId, transactionId, zipCode]
    );

    return [updateShipmentQuote];
}
